import { Navigate, useLocation, useNavigate } from "react-router";
import { useSession } from "../lib/auth-client";
import { readReturnTo, returnToPath } from "../lib/auth-return";

/**
 * Public landing page — the first thing an unauthenticated visitor sees at the
 * root (RequireAuth redirects anon users here, not straight to /login).
 *
 * Two reasons it exists:
 *  1. UX — a branded "this is Crosbe AI" page reads better than dropping a
 *     visitor onto a bare credential prompt.
 *  2. Reputation — the public face of the domain is now legitimate business
 *     content rather than a Microsoft sign-in, which removes the "fake login
 *     page" signal Google Safe Browsing flags on young domains (#460). NB: this
 *     does NOT itself clear a Safe Browsing flag (only a GSC review does) — it's
 *     hardening against re-flagging.
 *
 * No data, no auth — safe to be fully public. Authed users who land here are
 * sent into the app.
 */
const FEATURES = [
  "Production monitoring",
  "Quality control & SPC",
  "Lab testing & calibration",
  "Materials & cost analysis",
  "AI data analyst",
];

export function Landing() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = readReturnTo(location.search);

  // Already signed in → into the app, don't show the marketing page.
  if (!isPending && session?.user) return <Navigate to={returnTo} replace />;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl text-center space-y-8">
          <div className="space-y-4">
            <img
              src="/crosbe-logo.png"
              alt="Crosbe"
              className="mx-auto h-14 w-auto"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight">Crosbe AI</h1>
              <p className="text-base text-muted-foreground">
                Production Intelligence Platform
              </p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
            The internal business-intelligence platform for Crosbe — production,
            quality, lab and materials data with AI-assisted analysis, in one place.
          </p>

          <ul className="flex flex-wrap justify-center gap-2">
            {FEATURES.map((f) => (
              <li
                key={f}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground"
              >
                {f}
              </li>
            ))}
          </ul>

          <div className="pt-2">
            <button
              type="button"
              onClick={() => navigate(returnToPath("/login", returnTo))}
              className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Sign in
            </button>
            <p className="mt-3 text-xs text-muted-foreground">
              For authorised Crosbe staff.
            </p>
          </div>
        </div>
      </main>

      <footer className="px-6 py-4 text-center text-xs text-muted-foreground">
        Crosbe · Internal platform
      </footer>
    </div>
  );
}
